import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { ObjectType, isNotTransits, signs, getSign } from '@chronos/astro-lib';
import { getArrangeObjects, fortuneLon } from './Objects';
import { Group } from './Circle';
import { hideMapInfoPopup, showMapInfoPopup, intersectionFixedStarsArray } from './utils';
import { degToString } from '@chronos/api';
export default React.memo(function FixedStars(props) {
    const w3Url = 'http://www.w3.org/2000/svg';
    const _container = React.createRef();
    const { computedRadius, mapSettings: { personalization } } = props;
    const orbis = 1;
    const iconSize = props.radius * personalization.circle.objects.iconSize;
    let pointRadius = 0;
    let pointRadiusExt = 0;
    let objectsRadius = 0;
    if (props.hasExt) {
        pointRadius = props.radius * computedRadius.external;
    }
    else {
        pointRadius = props.radius * computedRadius.zodiacsExternal;
    }
    const offset = ['directions', 'compatibility', 'solars', 'transits', 'prog_natal'].includes(props.mode)
        ? 64
        : 24;
    const getAverageLon = (lons) => {
        let result = undefined;
        if (!(lons === null || lons === void 0 ? void 0 : lons.length))
            return result;
        return lons.reduce((acc, lon) => (acc + lon), 0) / lons.length;
    };
    const createStarGroup = (drawObject, radius) => {
        var _a;
        const { stars } = drawObject;
        const isWithCusp = drawObject.type === 'house';
        const isDouble = drawObject.type === 'double';
        const group = document.createElementNS(w3Url, 'g');
        const starIcon = document.createElementNS(w3Url, 'circle');
        const animation = document.createElementNS(w3Url, 'animate');
        animation.setAttribute('attributeName', 'opacity');
        animation.setAttribute('values', '0.3;1;0.3');
        animation.setAttribute('dur', `${Math.floor(Math.random() * (5 - 3 + 1)) + 3}s`);
        animation.setAttribute('repeatCount', 'indefinite');
        starIcon.append(animation);
        if (!stars.length)
            return group;
        const drawOffset = isWithCusp ? offset + (props.mode === 'horar' ? 16 : 21) : offset;
        const iconPoint = props.point(radius + drawOffset, drawObject.lon + (isWithCusp ? -3 : 0));
        const hasHighhlights = stars.some((star) => { var _a; return (_a = props.highlights) === null || _a === void 0 ? void 0 : _a.includes(`${star.en}_${star.lon}`); });
        starIcon.setAttribute('r', '10');
        starIcon.setAttribute('cx', iconPoint.x.toString());
        starIcon.setAttribute('cy', iconPoint.y.toString());
        starIcon.setAttribute('fill', `url(#circle-${(stars.length > 2 && !hasHighhlights) ? 'star3' : `star${hasHighhlights ? 1 : stars.length}`}${props.hasExt ? `-${props.mode === 'natal' ? 'red' : 'blue'}` : ''})`);
        const opacity = !props.highlights || hasHighhlights ? 1 : 0.1;
        group.style.opacity = `${opacity}`;
        const lines = drawObject.stars
            .sort((star1, star2) => {
            return (star1.drawLon > star2.drawLon) ? 1 : (star1.drawLon < star2.drawLon) ? -1 : 0;
        })
            .map((star) => {
            return `${props.mapSettings.language === "ru" ? star.ru : star.en}: ${degToString(star.lon % 30, { isInternational: false })}`;
        });
        starIcon.onmouseenter = (ev) => {
            showMapInfoPopup(ev.target, [`В соединении с ${isWithCusp ? 'куспидом:' : isDouble ? 'куспидом и планетой:' : 'планетой:'}`, ...lines], ev);
        };
        starIcon.onmouseleave = () => {
            hideMapInfoPopup();
        };
        group.append(starIcon);
        for (let star of drawObject.stars) {
            const starCircle = document.createElementNS(w3Url, 'circle');
            const point = props.point(radius, props.mode === 'horar' ? star.drawLon : star.lon);
            starCircle.setAttribute('r', '2.7');
            starCircle.setAttribute('cx', point.x.toString());
            starCircle.setAttribute('cy', point.y.toString());
            starCircle.setAttribute('fill', 'var(--workspace-background)');
            starCircle.setAttribute('stroke', 'var(--icon-primary)');
            starCircle.style.opacity = !props.highlights || ((_a = props.highlights) === null || _a === void 0 ? void 0 : _a.includes(`${star.en}_${star.lon}`)) ? '1' : '0.1';
            starCircle.onmouseenter = (ev) => {
                const lines = [
                    `${props.mapSettings.language === "ru" ? star.ru : star.en}: ${degToString(star.lon % 30, { isInternational: false })}`
                ];
                showMapInfoPopup(ev.target, lines, ev);
            };
            starCircle.onmouseleave = () => {
                hideMapInfoPopup();
            };
            group.append(starCircle);
        }
        return group;
    };
    React.useEffect(() => {
        var _a, _b, _c;
        if (!_container.current)
            return;
        const cuspConjuctions = [];
        const objectConjuctions = [];
        const doubleConjuctions = [];
        let tableObjCalcLon = [];
        const objCalcLon = (lon) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            let result = lon;
            for (let i = 1; i < tableObjCalcLon.length; i++) {
                const prevHouse = (_a = tableObjCalcLon[i - 1]) === null || _a === void 0 ? void 0 : _a.house;
                const currHouse = (_b = tableObjCalcLon[i]) === null || _b === void 0 ? void 0 : _b.house;
                const prevCuspLon = (_c = tableObjCalcLon[i - 1]) === null || _c === void 0 ? void 0 : _c.cuspLon;
                if (lon <= currHouse && lon >= prevHouse) {
                    result = tableObjCalcLon[i].cuspLon - (30 * (currHouse - lon)) / (currHouse - prevHouse);
                    return result;
                }
                if (lon >= currHouse && lon >= prevHouse && prevHouse >= currHouse) {
                    result = prevCuspLon + (30 * (lon - prevHouse)) / (360 - prevHouse + currHouse);
                    return result;
                }
                if (lon <= currHouse && prevHouse >= currHouse) {
                    result = prevCuspLon + (30 * (360 - prevHouse + lon)) / (360 - prevHouse + currHouse);
                    return result;
                }
            }
            if (lon < ((_d = tableObjCalcLon[0]) === null || _d === void 0 ? void 0 : _d.house)) {
                result = tableObjCalcLon[0].cuspLon - (30 * (((_e = tableObjCalcLon[0]) === null || _e === void 0 ? void 0 : _e.house) - lon)) / Math.abs(((_f = tableObjCalcLon[0]) === null || _f === void 0 ? void 0 : _f.house) - ((_g = tableObjCalcLon[tableObjCalcLon.length - 1]) === null || _g === void 0 ? void 0 : _g.house));
                return result;
            }
            if (lon > ((_h = tableObjCalcLon[tableObjCalcLon.length - 1]) === null || _h === void 0 ? void 0 : _h.house)) {
                result = tableObjCalcLon[tableObjCalcLon.length - 1].cuspLon + (30 * (lon - ((_j = tableObjCalcLon[tableObjCalcLon.length - 1]) === null || _j === void 0 ? void 0 : _j.house))) / (360 - ((_k = tableObjCalcLon[tableObjCalcLon.length - 1]) === null || _k === void 0 ? void 0 : _k.house) + ((_l = tableObjCalcLon[0]) === null || _l === void 0 ? void 0 : _l.house));
                return result;
            }
            return result;
        };
        props.houses.forEach((house, idx) => {
            var _a, _b, _c;
            const cuspLon = ['horar', 'soul'].includes(props.mode) ? (props.mode === 'soul' ? 0 : 180) + idx * 30 : props.houses[idx];
            tableObjCalcLon.push({ house, cuspLon });
            const stars = props.fixedStars.reduce((acc, star) => {
                if (Math.abs((star.lon) - house) <= orbis) {
                    star.drawLon = cuspLon;
                    acc.push(star);
                }
                return acc;
            }, []);
            if (stars.length) {
                cuspConjuctions.push({
                    type: 'house',
                    lon: cuspLon,
                    signId: getSign(house),
                    sign: (_c = (_b = (_a = props.mapSettings).t) === null || _b === void 0 ? void 0 : _b.call(_a, signs[getSign(house)].ru)) !== null && _c !== void 0 ? _c : signs[getSign(house)].ru,
                    stars,
                    iconName: `${stars.length === 1 ? 'star' : (stars.length === 2 ? 'two' : 'several')}`
                });
            }
            ;
        });
        const orderSize = props.mode === 'horar' ?
            3
            : props.mode === 'soul'
                ? 1
                : personalization.circle.objects.location === 1
                    ? Math.floor((objectsRadius - (props.isExt ? pointRadiusExt : pointRadius)) * 2 / (iconSize * 1.6)) || 1
                    : 1;
        let arrangeObjects = getArrangeObjects(props.objects
            .filter((obj, id) => (id !== ObjectType.Chiron || !isNotTransits(props.mode)) && (props.mode === 'soul' || id !== ObjectType.Selena))
            .map((obj, id) => {
            if (props.mode === 'soul' && id === ObjectType.SouthNode) {
                return {
                    ...obj,
                    lon: fortuneLon(props.objects, props.houses)
                };
            }
            return obj;
        }), props.houses, +(iconSize / 4).toFixed(2), props.horarSigns, orderSize, props.soulStrongs || [], props.mapSettings.astroSettings.showHigherPlanets);
        for (let object of arrangeObjects) {
            if ([ObjectType.Chiron, ObjectType.Lilith, ObjectType.SouthNode, ObjectType.NorthNode].includes(object.id) || (props.mode === 'horar' && object.id === ObjectType.Selena))
                continue;
            const isFortune = props.mode === 'soul' && object.id === ObjectType.SouthNode;
            const objectLon = isFortune ? fortuneLon(props.objects, props.houses.length ? props.houses : [0]) : props.objects[object.id].lon;
            const stars = props.fixedStars.reduce((acc, star) => {
                if (Math.abs(star.lon - objectLon) <= orbis) {
                    star.drawLon = props.mode === 'horar' ? objCalcLon(star.lon) : star.lon;
                    acc.push(star);
                }
                return acc;
            }, []);
            if (!stars.length)
                continue;
            objectConjuctions.push({
                type: 'object',
                lon: props.mode === 'horar' ? objCalcLon(getAverageLon(stars.map((star) => star.lon))) : getAverageLon(stars.map((star) => star.lon)),
                signId: getSign(objectLon),
                sign: (_c = (_b = (_a = props.mapSettings).t) === null || _b === void 0 ? void 0 : _b.call(_a, signs[getSign(objectLon)].ru)) !== null && _c !== void 0 ? _c : signs[getSign(objectLon)].ru,
                stars,
                iconName: `${stars.length === 1 ? 'star' : (stars.length === 2 ? 'two' : 'several')}`
            });
        }
        cuspConjuctions.forEach((cuspConjuction, i) => {
            let cuspStars = cuspConjuction.stars;
            objectConjuctions.forEach((objectConjuction, j) => {
                var _a, _b, _c;
                let objectStars = objectConjuction.stars;
                const common = intersectionFixedStarsArray(cuspStars, objectStars);
                if (common.length) {
                    const commonLon = common.map((s) => s.drawLon);
                    const lon = commonLon[0];
                    doubleConjuctions.push({
                        type: 'double',
                        lon: lon,
                        signId: getSign(lon),
                        sign: (_c = (_b = (_a = props.mapSettings).t) === null || _b === void 0 ? void 0 : _b.call(_a, signs[getSign(lon)].ru)) !== null && _c !== void 0 ? _c : signs[getSign(lon)].ru,
                        stars: common,
                        iconName: `${common.length === 1 ? 'star' : (common.length === 2 ? 'two' : 'several')}`
                    });
                    cuspConjuctions[i].stars = cuspStars.filter((star) => !commonLon.includes(star.drawLon));
                    objectConjuctions[j].stars = objectStars.filter((star) => !commonLon.includes(star.drawLon));
                }
            });
        });
        const drawResult = [
            ...cuspConjuctions,
            ...objectConjuctions,
            ...doubleConjuctions
        ];
        const drawGroup = document.createElementNS(w3Url, 'g');
        for (let drawObject of drawResult) {
            const svgStar = createStarGroup(drawObject, pointRadius);
            drawGroup.append(svgStar);
        }
        _container.current.innerHTML = '';
        _container.current.appendChild(drawGroup);
    }, [props.houses, props.objects, props.fixedStars, props.highlights, _container.current]);
    return (_jsx(Group, { id: "circle-fixed-stars", ref: _container }));
});
